import { z } from 'zod';

// Free text a referrer wrote about the referral, by audience — `internal`
// never leaves Perci, and the form says so on the field itself (PPL-3563).
//
//   notes: { internal: 'Member prefers afternoon calls' }
//
// A section rather than one `internalNote` field, so a note meant for the
// member or the partner can join it without moving anything — the same
// reasoning (and the same shape) as the consent comment, see
// ReferralConsentSchema.

// A referrer who tabbed through the box, or typed only spaces, wrote no note
const blankToUndefined = (value: unknown) =>
  typeof value === 'string' && value.trim() === '' ? undefined : value;

export const ReferralNotesSchema = z
  .object({
    internal: z.preprocess(
      blankToUndefined,
      z
        .string()
        .trim()
        .max(4000, 'Notes must be at most 4000 characters')
        .optional(),
    ),
  })
  // Nothing written: no notes to store, never an empty object
  .transform((notes) => (notes.internal ? notes : undefined))
  // `.optional()` LAST, wrapping the transform rather than sitting under it:
  // that is what tells the OpenAPI page the field may be left out (a transform
  // hides the optionality of what it wraps, so `notes` documented as REQUIRED)
  .optional()
  // ...and a transform is a rule no JSON Schema can express, so the shape a
  // caller may send is stated here rather than documenting as "anything"
  .meta({
    type: 'object',
    description:
      'Notes the referrer wrote about the referral. Internal to Perci: never shown to the member.',
    properties: { internal: { type: 'string', maxLength: 4000 } },
    additionalProperties: false,
  });

export type ReferralNotes = NonNullable<z.infer<typeof ReferralNotesSchema>>;
